import type { TFunction } from "i18next";
import { notificationEventGroups, type NotificationEventKey } from "../lib/notifications/preferences";

export type NotificationSettingsTranslate = TFunction<"notificationSettings">;

const groupMessages = {
  "applications": [
    "groupApplicationsTitle",
    "groupApplicationsDescription"
  ],
  "security": [
    "groupSecurityTitle",
    "groupSecurityDescription"
  ],
  "automation": [
    "groupAutomationTitle",
    "groupAutomationDescription"
  ],
  "support": [
    "groupSupportTitle",
    "groupSupportDescription"
  ],
  "digest": [
    "groupDigestTitle",
    "groupDigestDescription"
  ],
  "admin-fleet": [
    "groupAdminFleetTitle",
    "groupAdminFleetDescription"
  ],
  "transfers": [
    "groupTransfersTitle",
    "groupTransfersDescription"
  ]
} as const;

const eventMessages = {
  "app.offline": [
    "eventAppOfflineTitle",
    "eventAppOfflineDescription"
  ],
  "app.online": [
    "eventAppOnlineTitle",
    "eventAppOnlineDescription"
  ],
  "system.degraded": [
    "eventSystemDegradedTitle",
    "eventSystemDegradedDescription"
  ],
  "system.diskUsage": [
    "eventSystemDiskUsageTitle",
    "eventSystemDiskUsageDescription"
  ],
  "app.updateAvailable": [
    "eventAppUpdateAvailableTitle",
    "eventAppUpdateAvailableDescription"
  ],
  "qb.updateAvailable": [
    "eventQbUpdateAvailableTitle",
    "eventQbUpdateAvailableDescription"
  ],
  "dashboard.updateAvailable": [
    "eventDashboardUpdateAvailableTitle",
    "eventDashboardUpdateAvailableDescription"
  ],
  "signal.updateAvailable": [
    "eventSignalUpdateAvailableTitle",
    "eventSignalUpdateAvailableDescription"
  ],
  "app.updated": [
    "eventAppUpdatedTitle",
    "eventAppUpdatedDescription"
  ],
  "security.newLogin": [
    "eventSecurityNewLoginTitle",
    "eventSecurityNewLoginDescription"
  ],
  "security.sessionRevoked": [
    "eventSecuritySessionRevokedTitle",
    "eventSecuritySessionRevokedDescription"
  ],
  "security.passwordChanged": [
    "eventSecurityPasswordChangedTitle",
    "eventSecurityPasswordChangedDescription"
  ],
  "security.mfaChanged": [
    "eventSecurityMfaChangedTitle",
    "eventSecurityMfaChangedDescription"
  ],
  "job.completed": [
    "eventJobCompletedTitle",
    "eventJobCompletedDescription"
  ],
  "job.failed": [
    "eventJobFailedTitle",
    "eventJobFailedDescription"
  ],
  "support.ticketUpdated": [
    "eventSupportTicketUpdatedTitle",
    "eventSupportTicketUpdatedDescription"
  ],
  "support.chatMessage": [
    "eventSupportChatMessageTitle",
    "eventSupportChatMessageDescription"
  ],
  "digest.daily": [
    "eventDigestDailyTitle",
    "eventDigestDailyDescription"
  ],
  "digest.weekly": [
    "eventDigestWeeklyTitle",
    "eventDigestWeeklyDescription"
  ],
  "quietHours.enabled": [
    "eventQuietHoursEnabledTitle",
    "eventQuietHoursEnabledDescription"
  ],
  "admin.app.offline": [
    "eventAdminAppOfflineTitle",
    "eventAdminAppOfflineDescription"
  ],
  "admin.app.online": [
    "eventAdminAppOnlineTitle",
    "eventAdminAppOnlineDescription"
  ],
  "admin.vpn.killSwitch": [
    "eventAdminVpnKillSwitchTitle",
    "eventAdminVpnKillSwitchDescription"
  ],
  "admin.vpn.failback": [
    "eventAdminVpnFailbackTitle",
    "eventAdminVpnFailbackDescription"
  ],
  "admin.vpn.recovery": [
    "eventAdminVpnRecoveryTitle",
    "eventAdminVpnRecoveryDescription"
  ],
  "admin.monitor.alert": [
    "eventAdminMonitorAlertTitle",
    "eventAdminMonitorAlertDescription"
  ],
  "admin.phpFpm.offline": [
    "eventAdminPhpFpmOfflineTitle",
    "eventAdminPhpFpmOfflineDescription"
  ],
  "admin.phpFpm.online": [
    "eventAdminPhpFpmOnlineTitle",
    "eventAdminPhpFpmOnlineDescription"
  ],
  "admin.security.attackSpike": [
    "eventAdminSecurityAttackSpikeTitle",
    "eventAdminSecurityAttackSpikeDescription"
  ],
  "news.announcement": [
    "eventNewsAnnouncementTitle",
    "eventNewsAnnouncementDescription"
  ],
  "admin.transfer.activity": [
    "eventAdminTransferActivityTitle",
    "eventAdminTransferActivityDescription"
  ]
} as const satisfies Record<NotificationEventKey, readonly [string, string]>;

/** Localized copies retain the contract's event IDs, channel IDs and gating metadata. */
export function getNotificationEventGroups(t: NotificationSettingsTranslate) {
  return notificationEventGroups.map(group => {
    const keys = groupMessages[group.id as keyof typeof groupMessages];
    return {
      ...group,
      title: keys ? t($ => $[keys[0]]) : group.title,
      description: keys ? t($ => $[keys[1]]) : group.description,
      events: group.events.map(event => ({
        ...event,
        title: t($ => $[eventMessages[event.key][0]]),
        description: t($ => $[eventMessages[event.key][1]]),
      })),
    };
  });
}

export type DigestPreview = {
  digest?: {
    date: string;
    totalCount: number;
    apps: { name: string; installedVersion: string; latestVersion: string }[];
    qb: { name: string; installedVersion: string; latestVersion: string } | null;
    dashboard: { currentCommitShort: string; latestCommitShort: string } | null;
  } | null;
};

export function formatDigestHour(hour: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit", timeZone: "UTC" }).format(new Date(Date.UTC(2026, 0, 4, hour)));
}

export function formatDigestWeekday(weekday: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2026, 0, 4 + weekday)));
}

export function formatDigestPreview(digest: DigestPreview["digest"], t: NotificationSettingsTranslate, locale: string): string {
  if (!digest || digest.totalCount === 0) return t($ => $.previewEmpty);
  const lines: string[] = [t($ => $.previewCount, { count: digest.totalCount, replace: { count: new Intl.NumberFormat(locale).format(digest.totalCount) } })];
  if (digest.dashboard || digest.qb) lines.push("", t($ => $.previewPlatform));
  if (digest.dashboard) lines.push(`  • ${t($ => $.previewDashboard, { current: digest.dashboard.currentCommitShort, latest: digest.dashboard.latestCommitShort })}`);
  if (digest.qb) lines.push(`  • ${digest.qb.name}: ${digest.qb.installedVersion} → ${digest.qb.latestVersion}`);
  if (digest.apps.length) {
    lines.push("", t($ => $.previewApplications));
    for (const app of digest.apps) lines.push(`  • ${app.name}: ${app.installedVersion} → ${app.latestVersion}`);
  }
  return lines.join("\n");
}
